import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Building2,
  Check,
  CheckCircle2,
  Clock,
  FileText,
  Pencil,
  Percent,
  Plus,
  Printer,
  PowerOff,
  Scale,
  Trash2,
  XCircle,
  User as UsuarioIcono,
  X,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import type { Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type {
  Exoneracion,
  EstadoDeExoneracion,
  ResumenDeExoneracionesPorEstado,
  TipoDeExoneracion,
} from '@/shared/api/tipos';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import { AccionesDelFormulario, Campo, Modal, claseEntrada } from '@/shared/ui/Modal';
import { Tabla } from '@/shared/ui/Tabla';
import type { Columna } from '@/shared/ui/Tabla';
import { notificar } from '@/shared/ui/avisos';
import { DialogoDeExoneracion } from './DialogoDeExoneracion';
import { FichaDeExoneracion } from './FichaDeExoneracion';
import { ESTADOS } from './estadosDeExoneracion';
import { PestanasDeExoneraciones } from './PestanasDeExoneraciones';
import { ReporteDeExoneraciones } from './ReporteDeExoneraciones';

type Decision = { exoneracion: Exoneracion; accion: 'rechazar' | 'suspender' };

export function PaginaExoneraciones() {
  const clienteDeConsultas = useQueryClient();

  const [buscar, setBuscar] = useState('');
  const [estado, setEstado] = useState('');
  const [tipo, setTipo] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(10);
  const [editando, setEditando] = useState<Exoneracion | null>(null);
  const [creando, setCreando] = useState(false);
  const [decidiendo, setDecidiendo] = useState<Decision | null>(null);
  const [aEliminar, setAEliminar] = useState<Exoneracion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reportando, setReportando] = useState(false);
  const [enFicha, setEnFicha] = useState<Exoneracion | null>(null);

  const lista = useQuery({
    queryKey: ['exoneraciones', buscar, tipo, estado, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<Exoneracion>>('/exoneraciones', {
        query: {
          texto: buscar || undefined,
          tipo: tipo || undefined,
          estado: estado || undefined,
          pagina,
          tamanio,
        },
      }),
  });

  const resumen = useQuery({
    queryKey: ['exoneraciones-resumen'],
    queryFn: () => api.get<ResumenDeExoneracionesPorEstado>('/exoneraciones/resumen'),
  });

  const tipos = useQuery({
    queryKey: ['tipos-exoneracion'],
    queryFn: () => api.get<TipoDeExoneracion[]>('/tipos-exoneracion'),
  });

  const nombreDelTipo = (tipos.data ?? []).find((t) => t.codigo === tipo)?.nombre;

  function filtrarPorEstado(cual: EstadoDeExoneracion) {
    setEstado((actual) => (actual === cual ? '' : cual));
    setPagina(0);
  }

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['exoneraciones'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: ['exoneraciones-resumen'] });
  }

  function alFallar(causa: unknown, respaldo: string) {
    const mensaje = causa instanceof ErrorDeApi ? causa.message : respaldo;
    setError(mensaje);
    notificar.error(mensaje);
  }

  const aprobar = useMutation({
    mutationFn: (e: Exoneracion) => api.post<Exoneracion>(`/exoneraciones/${e.id}/aprobar`, {}),
    onSuccess: (_, e) => {
      refrescar();
      notificar.exito(`Exoneración aprobada: ${e.clienteNombre}`);
    },
    onError: (causa) => alFallar(causa, 'No se pudo aprobar'),
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/exoneraciones/${id}`),
    onSuccess: () => {
      setAEliminar(null);
      refrescar();
      notificar.exito('Solicitud eliminada');
    },
    onError: (causa) => alFallar(causa, 'No se pudo eliminar'),
  });

  const columnas: Columna<Exoneracion>[] = [
    {
      clave: 'beneficiario',
      encabezado: 'Beneficiario',
      contenido: (e) => (
        <div className="flex items-center gap-2.5">
          <span
            className={`grid size-8 shrink-0 place-items-center rounded-lg ${
              e.clienteIdentificacion.length === 13
                ? 'bg-amber-50 text-amber-600'
                : 'bg-indigo-50 text-indigo-500'
            }`}
          >
            {e.clienteIdentificacion.length === 13 ? (
              <Building2 className="size-3.5" aria-hidden />
            ) : (
              <UsuarioIcono className="size-3.5" aria-hidden />
            )}
          </span>
          <span className="min-w-0">
            <span className="block truncate font-semibold uppercase text-armazon-800">
              {e.clienteNombre}
            </span>
            <span className="importe block text-[11px] text-armazon-500">
              {e.clienteIdentificacion}
            </span>
          </span>
        </div>
      ),
    },
    {
      clave: 'alcance',
      encabezado: 'Alcance',
      clase: 'w-40',
      contenido: (e) =>
        e.general ? (
          <span className="text-[11px] uppercase text-armazon-500">Todas sus cuentas</span>
        ) : (
          <span className="importe truncate text-xs text-armazon-700">{e.numeroCuenta}</span>
        ),
    },
    {
      clave: 'tipo',
      encabezado: 'Tipo',
      clase: 'w-56',
      contenido: (e) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs uppercase text-armazon-700">{e.tipoNombre}</span>

          {e.gradoDiscapacidad != null && (
            <span className="importe flex items-center gap-1 text-[10px] text-armazon-400">
              <Scale className="size-2.5 shrink-0" aria-hidden />
              Grado {e.gradoDiscapacidad} %
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'descuento',
      encabezado: 'Descuento',
      alinear: 'derecha',
      clase: 'w-24',
      contenido: (e) => (
        <span className="importe text-sm font-bold text-indigo-600">{e.porcentaje} %</span>
      ),
    },
    {
      clave: 'vigencia',
      encabezado: 'Vigencia',
      clase: 'w-36',
      contenido: (e) => (
        <span className="importe block text-[11px] text-armazon-600">
          {e.fechaInicio}
          <span className="block text-armazon-400">
            {e.indefinida ? 'Vigencia indefinida' : `Hasta ${e.fechaFin}`}
          </span>
        </span>
      ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      alinear: 'centro',
      clase: 'w-32',
      contenido: (e) => {
        const s = ESTADOS[e.estado];
        return (
          <span className="inline-flex flex-col items-center gap-0.5">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase ring-1 ${s.clase}`}
              title={s.descuenta ? 'Está descontando' : 'No descuenta'}
            >
              <span className={`size-1.5 rounded-full ${s.punto}`} aria-hidden />
              {s.etiqueta}
            </span>

            {e.revisadoPor && (
              <span className="max-w-full truncate text-[10px] uppercase text-armazon-400">
                {s.descuenta ? 'Aprobó' : 'Revisó'}: {e.revisadoPor}
              </span>
            )}
          </span>
        );
      },
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      clase: 'w-40 pr-5',
      contenido: (e) => (
        <div className="flex justify-center gap-1">

          <BotonDeAccion
            icono={FileText}
            titulo="Ficha de la exoneración"
            nombre={`Ficha de la exoneración de ${e.clienteNombre}`}
            alPulsar={() => setEnFicha(e)}
            tono="gris"
          />

          {e.estado === 'pendiente' && (
            <>
              <BotonDeAccion
                icono={Check}
                titulo="Aprobar"
                nombre={`Aprobar la exoneración de ${e.clienteNombre}`}
                alPulsar={() => aprobar.mutate(e)}
                tono="verde"
              />
              <BotonDeAccion
                icono={X}
                titulo="Rechazar"
                nombre={`Rechazar la exoneración de ${e.clienteNombre}`}
                alPulsar={() => setDecidiendo({ exoneracion: e, accion: 'rechazar' })}
                tono="rojo"
              />
              <BotonDeAccion
                icono={Pencil}
                titulo="Editar"
                nombre={`Editar la exoneración de ${e.clienteNombre}`}
                alPulsar={() => setEditando(e)}
                tono="azul"
              />
            </>
          )}

          {e.estado === 'aprobada' && (
            <BotonDeAccion
              icono={PowerOff}
              titulo="Suspender"
              nombre={`Suspender la exoneración de ${e.clienteNombre}`}
              alPulsar={() => setDecidiendo({ exoneracion: e, accion: 'suspender' })}
              tono="ambar"
            />
          )}

          {(e.estado === 'pendiente' || e.estado === 'rechazada') && (
            <BotonDeAccion
              icono={Trash2}
              titulo="Eliminar"
              nombre={`Eliminar la exoneración de ${e.clienteNombre}`}
              alPulsar={() => setAEliminar(e)}
              tono="rojo"
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={Percent}
        color="indigo"
        titulo="Exoneraciones"
        subtitulo="Quién paga menos de la tasa, cuánto menos y con qué respaldo"
        acciones={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setReportando(true)}
              title="Reporte de exoneraciones"
              aria-label="Reporte de exoneraciones"
              className="grid size-11 place-items-center rounded-xl border border-armazon-200 bg-white text-armazon-600 shadow-sm transition-colors hover:border-marca-300 hover:text-marca-600"
            >
              <Printer className="size-5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setCreando(true)}
              title="Nueva exoneración"
              aria-label="Nueva exoneración"
              className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
            >
              <Plus className="size-5" aria-hidden />
            </button>
          </div>
        }
      />

      <PestanasDeExoneraciones />

      {resumen.data && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <TarjetaDeEstado
            icono={Clock}
            valor={resumen.data.pendientes}
            rotulo="Pendientes"
            estado="pendiente"
            activo={estado === 'pendiente'}
            alPulsar={filtrarPorEstado}
          />
          <TarjetaDeEstado
            icono={CheckCircle2}
            valor={resumen.data.aprobadas}
            rotulo="Aprobadas"
            estado="aprobada"
            activo={estado === 'aprobada'}
            alPulsar={filtrarPorEstado}
          />
          <TarjetaDeEstado
            icono={XCircle}
            valor={resumen.data.rechazadas}
            rotulo="Rechazadas"
            estado="rechazada"
            activo={estado === 'rechazada'}
            alPulsar={filtrarPorEstado}
          />
          <TarjetaDeEstado
            icono={Ban}
            valor={resumen.data.suspendidas}
            rotulo="Suspendidas"
            estado="suspendida"
            activo={estado === 'suspendida'}
            alPulsar={filtrarPorEstado}
          />
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <Tabla
        datos={lista.data}
        cargando={lista.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(n) => {
          setTamanio(n);
          setPagina(0);
        }}
        iconoVacio={Percent}
        mensajeVacio="NINGUNA EXONERACIÓN COINCIDE CON LA BÚSQUEDA"
        entidad="exoneraciones"
        herramientas={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <select
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value);
                setPagina(0);
              }}
              aria-label="Filtrar por tipo"
              className="h-10 rounded-lg border border-armazon-200 bg-white px-3 text-sm uppercase text-armazon-700 outline-none transition-colors focus:border-marca-400"
            >
              <option value="">Todos los tipos</option>
              {(tipos.data ?? [])
                .filter((t) => t.activo)
                .map((t) => (
                  <option key={t.codigo} value={t.codigo}>
                    {t.nombre}
                  </option>
                ))}
            </select>
            <select
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value);
                setPagina(0);
              }}
              aria-label="Filtrar por estado"
              className="h-10 rounded-lg border border-armazon-200 bg-white px-3 text-sm uppercase text-armazon-700 outline-none transition-colors focus:border-marca-400"
            >
              <option value="">Todos los estados</option>
              {Object.entries(ESTADOS).map(([clave, s]) => (
                <option key={clave} value={clave}>
                  {s.etiqueta}
                </option>
              ))}
            </select>
            <CampoDeBusqueda
              valor={buscar}
              alCambiar={(v) => {
                setBuscar(v);
                setPagina(0);
              }}
              marcador="Buscar por beneficiario…"
              className="w-full max-w-sm"
            />
          </div>
        }
      />

      {enFicha && (
        <FichaDeExoneracion exoneracion={enFicha} alCerrar={() => setEnFicha(null)} />
      )}

      {reportando && (
        <ReporteDeExoneraciones
          filtros={{
            texto: buscar,
            tipo,
            ...(nombreDelTipo ? { tipoNombre: nombreDelTipo } : {}),
            estado,
          }}
          alCerrar={() => setReportando(false)}
        />
      )}

      <DialogoDeExoneracion
        key={editando?.id ?? 'nueva'}
        abierto={creando || editando !== null}
        exoneracion={editando}
        tipos={tipos.data ?? []}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />

      {decidiendo !== null && (
        <DialogoDeDecision
          decision={decidiendo}
          alCerrar={() => setDecidiendo(null)}
          alDecidir={refrescar}
        />
      )}

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        alCerrar={() => {
          setAEliminar(null);
          setError(null);
        }}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        titulo="Eliminar solicitud"
        descripcion="Solo se eliminan las que nunca llegaron a descontar:"
        detalle={aEliminar ? `${aEliminar.clienteNombre} · ${aEliminar.tipoNombre}` : undefined}
        error={eliminar.isError ? error : null}
      />
    </>
  );
}

function DialogoDeDecision({
  decision,
  alCerrar,
  alDecidir,
}: {
  decision: Decision;
  alCerrar: () => void;
  alDecidir: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const { exoneracion, accion } = decision;
  const rechazando = accion === 'rechazar';

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await api.post<Exoneracion>(`/exoneraciones/${exoneracion.id}/${accion}`, { motivo });
      alDecidir();
      alCerrar();
      notificar.exito(
        `Exoneración ${rechazando ? 'rechazada' : 'suspendida'}: ${exoneracion.clienteNombre}`,
      );
    } catch (causa) {
      const mensaje =
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo completar la operación';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo={rechazando ? 'Rechazar solicitud' : 'Suspender exoneración'}
      subtitulo={`${exoneracion.clienteNombre} · ${exoneracion.tipoNombre}`}
      icono={rechazando ? X : PowerOff}
      cerrarAlPulsarFuera={false}
    >
      <form onSubmit={enviar} className="space-y-4">
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {error}
          </p>
        )}

        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] uppercase leading-relaxed text-amber-900">
          {rechazando
            ? 'La solicitud queda rechazada y no descuenta nada. El beneficiario puede volver a solicitarla.'
            : 'Deja de descontar desde la próxima emisión. Lo ya facturado con el descuento no se toca: es lo que se cobró.'}
        </p>

        <Campo
          etiqueta="Motivo"
          requerido
          ayuda="QUEDA GUARDADO EN LA EXONERACIÓN Y SE VE AL CONSULTARLA"
        >
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            minLength={5}
            maxLength={500}
            required
            autoFocus
            className={`${claseEntrada} resize-none`}
            placeholder={
              rechazando
                ? 'No presentó el certificado del Ministerio de Salud Pública'
                : 'El beneficiario falleció'
            }
          />
        </Campo>

        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={enviando || motivo.trim().length < 5}
          textoEnviar={rechazando ? 'Rechazar solicitud' : 'Suspender exoneración'}
        />
      </form>
    </Modal>
  );
}

function TarjetaDeEstado({
  icono: Icono,
  valor,
  rotulo,
  estado,
  activo,
  alPulsar,
}: {
  icono: typeof Clock;
  valor: number;
  rotulo: string;
  estado: EstadoDeExoneracion;
  activo: boolean;
  alPulsar: (estado: EstadoDeExoneracion) => void;
}) {
  const s = ESTADOS[estado];

  return (
    <button
      type="button"
      onClick={() => alPulsar(estado)}
      aria-pressed={activo}
      title={activo ? `Quitar el filtro de ${rotulo.toLowerCase()}` : `Ver solo las ${rotulo.toLowerCase()}`}
      className={`rounded-xl border bg-white p-4 text-left transition-shadow hover:shadow-md ${
        activo ? 'border-marca-300 ring-2 ring-marca-100' : 'border-armazon-200'
      }`}
    >
      <span className={`mb-2 grid size-8 place-items-center rounded-lg ${s.clase}`}>
        <Icono className="size-4" aria-hidden />
      </span>
      <span className={`importe block text-2xl font-bold ${TONO_TARJETA[estado]}`}>{valor}</span>
      <span className="block text-[10px] font-medium uppercase text-armazon-400">{rotulo}</span>
    </button>
  );
}

const TONO_TARJETA: Record<EstadoDeExoneracion, string> = {
  pendiente: 'text-amber-600',
  aprobada: 'text-emerald-600',
  rechazada: 'text-red-500',
  suspendida: 'text-armazon-500',
};

const TONO_ACCION = {
  verde: 'hover:bg-emerald-50 hover:text-emerald-600',
  rojo: 'hover:bg-red-50 hover:text-alerta',
  azul: 'hover:bg-blue-50 hover:text-blue-600',
  ambar: 'hover:bg-amber-50 hover:text-amber-600',
  gris: 'hover:bg-armazon-100 hover:text-armazon-700',
} as const;

function BotonDeAccion({
  icono: Icono,
  titulo,
  nombre,
  alPulsar,
  tono,
}: {
  icono: typeof Check;
  titulo: string;
  nombre: string;
  alPulsar: () => void;
  tono: keyof typeof TONO_ACCION;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      title={titulo}
      aria-label={nombre}
      className={`rounded-lg p-1.5 text-armazon-400 transition-colors ${TONO_ACCION[tono]}`}
    >
      <Icono className="size-[15px]" aria-hidden />
    </button>
  );
}
